import { Signal, useSignal } from '@preact/signals'

import {
	Button,
	HorizontalDivider,
	Typography,
	Modal,
	TextLink,
	TrashIcon,
	RightArrowIcon
} from 'components'

const mockupTeams = [
	{
		title: 'Testing',
		subtitle: 'Member'
	},
	{
		title: 'CodigoAI-ext',
		subtitle: 'Collaborator on 2 dApps'
	},
	{
		title: 'BlueDAO',
		subtitle: 'Member and collaborator on 23 dApps'
	}
]

const Section = () => (
	<div class='rounded-md border border-black-800 bg-black-850 p-5'>
		<Typography
			color='text-black-200'
			text='We use Código internally to prototype new types of Assistants before pushing them to production. It allows us to rapidly deploy our environment and test new features, ensuring they work seamlessly in mainnet.'
			variant='text2'
		/>
		<Typography
			class='mt-6'
			color='text-black-100'
			text='Jane Doe'
			variant='h5'
			weight='font-bold'
		/>
		<Typography
			class='mt-1'
			color='text-black-200'
			text='Co-founder, CONTOSO'
			variant='text2'
		/>
	</div>
)

type Props = {
	title: string
	subtitle: string
	onRemove?: () => void
	onLeave?: () => void
}

type PropModal = {
	selectedTeam: Signal<string>
	isModalOpen: Signal<boolean>
	isRemoveMemberButtonDisabled: boolean
	isRemoveMemberButtonLoading: boolean
	onCloseRemoveMemberModal: () => void
	removeMember: () => void
}

const RemoveMemberModal = ({
	selectedTeam,
	isModalOpen,
	isRemoveMemberButtonDisabled,
	isRemoveMemberButtonLoading,
	onCloseRemoveMemberModal,
	removeMember
}: PropModal) => (
	<Modal
		className='space-y-10 p-10'
		isOpen={isModalOpen.value}
		onCloseModal={onCloseRemoveMemberModal}
	>
		<Typography color='text-product' text='Remove Team' variant='h2' />

		<div>
			<div className='inline-flex gap-1'>
				<Typography
					color='text-black-100'
					text='Are you sure you want to remove '
					variant='text1'
				/>
				<Typography
					color='text-product'
					text={`${selectedTeam.value}?`}
					variant='text1'
				/>
			</div>
			<Typography
				color='text-black-100'
				text="Removing a team will delete team members' access to this data, and it cannot be recovered."
				variant='text1'
			/>
		</div>

		<div class='flex justify-between'>
			<TextLink
				ariaLabel='Cancel'
				onClick={onCloseRemoveMemberModal}
				text='Cancel'
				variant='secondary'
			/>

			<Button
				disabled={isRemoveMemberButtonDisabled}
				isLoading={isRemoveMemberButtonLoading}
				onClick={removeMember}
				text='Remove'
				variant='error'
			/>
		</div>
	</Modal>
)

type PropModalWantToLeave = {
	selectedTeam: Signal<string>
	isModalOpen: Signal<boolean>
	isWantToLeaveButtonDisabled: boolean
	isWantToLeaveButtonLoading: boolean
	onCloseWantToLeaveModal: () => void
	leaveTeam: () => void
}

const WantToLeaveModal = ({
	isModalOpen,
	isWantToLeaveButtonDisabled,
	isWantToLeaveButtonLoading,
	onCloseWantToLeaveModal,
	leaveTeam,
	selectedTeam
}: PropModalWantToLeave) => (
	<Modal
		className='space-y-10 p-10'
		isOpen={isModalOpen.value}
		onCloseModal={onCloseWantToLeaveModal}
	>
		<Typography color='text-product' text='Want to leave?' variant='h2' />

		<div>
			<div className='inline-flex gap-1'>
				<Typography
					color='text-black-100'
					text={`Are you sure you want to leave `}
					variant='text1'
				/>
				<Typography
					color='text-product'
					text={`${selectedTeam.value}?`}
					variant='text1'
				/>
			</div>
			<Typography
				color='text-black-100'
				text='Leaving an organization will remove your access to its projects and resources.'
				variant='text1'
			/>
		</div>

		<div class='flex justify-between'>
			<TextLink
				ariaLabel='Cancel'
				onClick={onCloseWantToLeaveModal}
				text='Cancel'
				variant='secondary'
			/>

			<Button
				disabled={isWantToLeaveButtonDisabled}
				isLoading={isWantToLeaveButtonLoading}
				onClick={leaveTeam}
				text='Leave'
				variant='error'
			/>
		</div>
	</Modal>
)

const Card = ({ title, subtitle, onRemove, onLeave }: Props) => (
	<div
		class='group mb-3 flex cursor-pointer justify-between rounded-md border border-black-800 bg-gradient-to-r from-black-800 to-black-900 p-4 transition-all hover:bg-black-850 hover:from-transparent hover:to-transparent'
		style={{
			background: 'linear-gradient(90deg, #323437 -16.84%, #151515 23.32%)'
		}}
	>
		<div>
			<Typography
				class='group-hover:underline'
				color='text-black-100'
				text={title}
				variant='text1'
			/>
			<div class='mt-4 flex space-x-4 text-sm text-gray-400'>
				<Typography
					class='inline-block'
					color='text-black-200'
					text={subtitle}
					variant='text3'
				/>
			</div>
		</div>
		<div class='flex flex-col justify-between'>
			<button
				aria-label={`Remove team ${title}`}
				class='text-black-100'
				onClick={onRemove}
				type='button'
			>
				<TrashIcon />
			</button>
			<button
				aria-label={`Leave team ${title}`}
				class='text-black-100'
				onClick={onLeave}
				type='button'
			>
				<RightArrowIcon />
			</button>
		</div>
	</div>
)

const TeamsPageWithInfo = () => {
	const isRemoveMemberModalOpen = useSignal(false)
	const isWantToLeaveModalOpen = useSignal(false)
	const selectedTeam = useSignal('')

	const handleOpenRemoveMemberModal = (teamName: string) => {
		selectedTeam.value = teamName
		isRemoveMemberModalOpen.value = true
	}

	const handleOpenWantToLeaveModal = (teamName: string) => {
		selectedTeam.value = teamName
		isWantToLeaveModalOpen.value = true
	}

	const handleCloseRemoveMemberModal = () => {
		isRemoveMemberModalOpen.value = false
	}

	const handleRemoveMember = () => {
		isRemoveMemberModalOpen.value = false
	}

	const handleCloseWantToLeaveModal = () => {
		isWantToLeaveModalOpen.value = false
	}

	const handleLeaveTeam = () => {
		isWantToLeaveModalOpen.value = false
	}

	return (
		<>
			<div class='flex grid-cols-1 flex-col-reverse gap-10 xl:grid xl:grid-cols-[2fr_1fr]'>
				<div class='flex-1'>
					<Typography color='text-black-100' text='Teams' variant='h3' />

					<div className='mt-5 flex flex-row items-start gap-4'>
						<Typography
							color='text-black-200'
							text="Unlock management tools, providing enhanced coordination, access control, and productivity features tailored to support your Organization's needs."
							variant='text1'
						/>

						{mockupTeams.length ? (
							<Button className='flex-shrink-0' text='Create Team' />
						) : null}
					</div>

					<HorizontalDivider className='my-10' />

					{mockupTeams.length === 0 ? (
						<div>
							<Typography
								color='text-black-100'
								text='Your Teams'
								variant='h4'
							/>
							<Typography
								class='mt-5'
								color='text-black-200'
								text='You have nothing created yet.'
								variant='text1'
							/>

							<Button className='mt-10' text='Create Team' />
						</div>
					) : (
						mockupTeams.map(team => (
							<Card
								key={team.title}
								onLeave={() => handleOpenWantToLeaveModal(team.title)}
								onRemove={() => handleOpenRemoveMemberModal(team.title)}
								subtitle={team.subtitle}
								title={team.title}
							/>
						))
					)}
				</div>

				<div>
					<Section />
				</div>
			</div>

			<RemoveMemberModal
				isModalOpen={isRemoveMemberModalOpen}
				isRemoveMemberButtonDisabled={false}
				isRemoveMemberButtonLoading={false}
				onCloseRemoveMemberModal={handleCloseRemoveMemberModal}
				removeMember={handleRemoveMember}
				selectedTeam={selectedTeam}
			/>

			<WantToLeaveModal
				isModalOpen={isWantToLeaveModalOpen}
				isWantToLeaveButtonDisabled={false}
				isWantToLeaveButtonLoading={false}
				leaveTeam={handleLeaveTeam}
				onCloseWantToLeaveModal={handleCloseWantToLeaveModal}
				selectedTeam={selectedTeam}
			/>
		</>
	)
}

export { TeamsPageWithInfo }
